import { useCallback, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { email, required, runValidations } from '@coursera/coursera-ui/lib/utils/validation';

import { checkForcedSsoLogin } from 'bundles/third-party-auth/api';
import { type CheckForcedSsoLoginResponse } from 'bundles/third-party-auth/types/responseTypes';

export const INPUT_DEBOUNCE_TIME = 300;

export type EnterpriseSsoLoginInfo = {
  isEnterpriseSsoLogin: boolean;
  redirectUrl: string;
  isLoading: boolean;
  isRedirecting: boolean;
};

const enterpriseSsoLoginInfoInitialState: EnterpriseSsoLoginInfo = {
  isEnterpriseSsoLogin: false,
  redirectUrl: '',
  isLoading: false,
  isRedirecting: false,
};

type UseEnterpriseSsoCheckReturn = {
  enterpriseSsoLoginInfo: EnterpriseSsoLoginInfo;
  checkEnterpriseSso: (email: string) => void;
  setIsRedirecting: (isRedirecting: boolean) => void;
  resetEnterpriseSsoInfo: () => void;
};

const useEnterpriseSsoCheck = (): UseEnterpriseSsoCheckReturn => {
  const [enterpriseSsoLoginInfo, setEnterpriseSsoLoginInfo] = useState<EnterpriseSsoLoginInfo>(
    enterpriseSsoLoginInfoInitialState
  );

  const resetEnterpriseSsoInfo = useCallback<VoidFunction>(() => {
    setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
  }, []);

  const setIsRedirecting = useCallback<(x: boolean) => void>((isRedirecting: boolean) => {
    setEnterpriseSsoLoginInfo((state) => ({ ...state, isRedirecting }));
  }, []);

  const checkForceSSO = useCallback<(emailField: string) => void>((emailField: string) => {
    const errors = runValidations(emailField, [required, email]);
    if (!errors) {
      setEnterpriseSsoLoginInfo((state) => ({ ...state, isLoading: true }));
      checkForcedSsoLogin(emailField)
        .then((response: CheckForcedSsoLoginResponse) => {
          if (response.isForceSSOLogin) {
            setEnterpriseSsoLoginInfo((state) => ({
              ...state,
              isEnterpriseSsoLogin: true,
              isLoading: false,
              redirectUrl: response.redirectUrl,
            }));
          } else {
            setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
          }
        })
        .catch(() => {
          setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
        });
    } else {
      setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
    }
  }, []);

  const debouncedCheckForceSSO = useMemo(() => debounce(checkForceSSO, INPUT_DEBOUNCE_TIME), [checkForceSSO]);

  const checkEnterpriseSso = useCallback<(emailField: string) => void>(
    (emailField) => {
      debouncedCheckForceSSO(emailField);
      return () => debouncedCheckForceSSO.cancel();
    },
    [debouncedCheckForceSSO]
  );

  return {
    enterpriseSsoLoginInfo,
    checkEnterpriseSso,
    setIsRedirecting,
    resetEnterpriseSsoInfo,
  };
};

export default useEnterpriseSsoCheck;
